import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { X, Upload, Trash2 } from 'lucide-react';

const OfflineJobForm = ({ onClose, initialData = null, onSuccess }) => {
  const { user } = useAuth();
  const { pets, addOfflineJob, updateOfflineJob } = useData();
  
  const [formData, setFormData] = useState(initialData || {
    jobDate: new Date().toISOString().split('T')[0],
    serviceType: 'boarding',
    petName: '',
    petId: null,
    clientName: '',
    clientId: null,
    duration: '',
    durationUnit: 'hours',
    amount: '',
    taxAmount: 0,
    paymentMethod: 'cash',
    receiptPhotos: [],
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const userPets = pets.filter(pet => pet.ownerId === user?.id);

  const serviceTypes = [
    { value: 'boarding', label: 'Boarding' },
    { value: 'walking', label: 'Walking' },
    { value: 'grooming', label: 'Grooming' },
    { value: 'sitting', label: 'Pet Sitting' },
    { value: 'training', label: 'Training' },
    { value: 'other', label: 'Other' }
  ];

  const paymentMethods = [
    { value: 'cash', label: 'Cash' },
    { value: 'upi', label: 'UPI' },
    { value: 'card', label: 'Card' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'other', label: 'Other' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePetSelection = (e) => {
    const petId = e.target.value;
    if (petId) {
      const selectedPet = userPets.find(p => p.id === petId);
      setFormData(prev => ({
        ...prev,
        petId: selectedPet.id,
        petName: selectedPet.name
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        petId: null,
        petName: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.jobDate) newErrors.jobDate = 'Job date is required';
    if (!formData.serviceType) newErrors.serviceType = 'Service type is required';
    if (!formData.petName.trim()) newErrors.petName = 'Pet name is required';
    if (!formData.clientName.trim()) newErrors.clientName = 'Client/Caregiver name is required';
    if (!formData.duration || formData.duration <= 0) newErrors.duration = 'Duration must be greater than 0';
    if (!formData.amount || formData.amount <= 0) newErrors.amount = 'Amount must be greater than 0';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setIsSubmitting(true);
    
    try {
      const jobData = {
        ...formData,
        userId: user.uid || user.id, // Use uid first (Firebase Auth), fallback to id
        userRole: user.role,
        duration: parseFloat(formData.duration),
        amount: parseFloat(formData.amount),
        taxAmount: parseFloat(formData.taxAmount || 0)
      };

      console.log('💾 Saving offline job:', jobData);

      let result;
      if (initialData) {
        result = await updateOfflineJob(initialData.id, jobData);
        console.log('✅ Update result:', result);
      } else {
        result = await addOfflineJob(jobData);
        console.log('✅ Create result:', result);
      }
      
      // Check if save was successful
      if (result && result.success === false) {
        throw new Error(result.error || 'Failed to save job');
      }
      
      console.log('✅ Offline job saved successfully!');
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('❌ Error saving offline job:', error);
      setErrors({ submit: error.message || 'Failed to save job. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl max-w-2xl w-full my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {initialData ? 'Edit Offline Job' : 'Add Offline Job'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Date & Service Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="jobDate"
                value={formData.jobDate}
                onChange={handleChange}
                max={new Date().toISOString().split('T')[0]}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.jobDate ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.jobDate && <p className="text-red-500 text-sm mt-1">{errors.jobDate}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Service Type <span className="text-red-500">*</span>
              </label>
              <select
                name="serviceType"
                value={formData.serviceType}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.serviceType ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                {serviceTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              {errors.serviceType && <p className="text-red-500 text-sm mt-1">{errors.serviceType}</p>}
            </div>
          </div>

          {/* Pet Selection */}
          {user.role === 'owner' && userPets.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Pet (or enter manually below)
              </label>
              <select
                value={formData.petId || ''}
                onChange={handlePetSelection}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">-- Select a pet or enter manually --</option>
                {userPets.map(pet => (
                  <option key={pet.id} value={pet.id}>{pet.name} ({pet.breed})</option>
                ))}
              </select>
            </div>
          )}

          {/* Pet Name & Client Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pet Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="petName"
                value={formData.petName}
                onChange={handleChange}
                placeholder="Enter pet name"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.petName ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.petName && <p className="text-red-500 text-sm mt-1">{errors.petName}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {user.role === 'caregiver' ? 'Client Name' : 'Caregiver/Provider Name'} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="clientName"
                value={formData.clientName}
                onChange={handleChange}
                placeholder={user.role === 'caregiver' ? 'Enter client name' : 'Enter caregiver/provider name'}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.clientName ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.clientName && <p className="text-red-500 text-sm mt-1">{errors.clientName}</p>}
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Duration <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                placeholder="Enter duration"
                min="0"
                step="0.5"
                className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.duration ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              <select
                name="durationUnit"
                value={formData.durationUnit}
                onChange={handleChange}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="hours">Hours</option>
                <option value="days">Days</option>
              </select>
            </div>
            {errors.duration && <p className="text-red-500 text-sm mt-1">{errors.duration}</p>}
          </div>

          {/* Amount & Tax */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount (₹) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="Enter amount"
                min="0"
                step="0.01"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.amount ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.amount && <p className="text-red-500 text-sm mt-1">{errors.amount}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tax Amount (₹)
              </label>
              <input
                type="number"
                name="taxAmount"
                value={formData.taxAmount}
                onChange={handleChange}
                placeholder="Enter tax amount"
                min="0"
                step="0.01"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Total Display */}
          {formData.amount && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Total Amount:</span>
                <span className="text-xl font-bold text-blue-600">
                  ₹{(parseFloat(formData.amount || 0) + parseFloat(formData.taxAmount || 0)).toLocaleString()}
                </span>
              </div>
            </div>
          )}

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Method <span className="text-red-500">*</span>
            </label>
            <select
              name="paymentMethod"
              value={formData.paymentMethod}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {paymentMethods.map(method => (
                <option key={method.value} value={method.value}>{method.label}</option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Add any additional notes..."
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Receipt Photos (placeholder for future implementation) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Receipt Photos (Coming Soon)
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center text-gray-500">
              <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              <p className="text-sm">Photo upload will be available in the next update</p>
            </div>
          </div>

          {/* Error Message */}
          {errors.submit && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{errors.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving...' : (initialData ? 'Update Job' : 'Add Job')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OfflineJobForm;

